/** Custom fields of a row, from its `fields` JSON object. */
export function parseFields(json) {
    try {
        const parsed = JSON.parse(json ?? '{}');
        return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
            ? parsed
            : {};
    }
    catch {
        return {};
    }
}
/** Value of a sort or overdue field: a column, or `field:<key>` for a custom field. */
function valueOf(item, field) {
    if (field.startsWith('field:'))
        return item.fieldValues[field.slice('field:'.length)];
    return item[field];
}
export function parseTags(json) {
    try {
        const parsed = JSON.parse(json);
        return Array.isArray(parsed) ? parsed : [];
    }
    catch {
        return [];
    }
}
function compareBy(sort) {
    return (a, b) => {
        for (const key of sort) {
            const av = valueOf(a, key.field);
            const bv = valueOf(b, key.field);
            const aNull = av === null || av === undefined;
            const bNull = bv === null || bv === undefined;
            if (aNull || bNull) {
                if (aNull && bNull)
                    continue;
                const nullsLast = (key.nulls ?? 'last') === 'last';
                return aNull === nullsLast ? 1 : -1;
            }
            // Sort keys compare bytewise (docs/08 §8); dates as ISO strings.
            if (av !== bv) {
                const order = av < bv ? -1 : 1;
                return key.dir === 'desc' ? -order : order;
            }
        }
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    };
}
const matches = (item, selected) => selected.length === 0 ||
    (item.tagList.length === 0
        ? selected.includes(null)
        : item.tagList.some((tag) => selected.includes(tag.id)));
export function buildViewModel(input) {
    const groupField = input.groupBy?.startsWith('field:')
        ? input.groupBy.slice('field:'.length)
        : null;
    const byId = new Map();
    for (const row of input.rows) {
        const fieldValues = parseFields(row.fields);
        let tagList;
        if (groupField) {
            const id = fieldValues[groupField];
            const name = fieldValues[`${groupField}.name`];
            tagList = id && name ? [{ id, name, sort_key: name }] : [];
        }
        else {
            tagList = parseTags(row.tags);
        }
        const item = {
            ...row,
            tagList,
            fieldValues,
            done: row.status === 'done',
            overdue: false,
        };
        if (input.overdue && !item.done) {
            const value = valueOf(item, input.overdue.field);
            item.overdue = typeof value === 'string' && value <= input.overdue.today;
        }
        byId.set(row.id, item);
    }
    const all = [...byId.values()].sort(compareBy(input.sort));
    // Chips: every context of the rows, with its open tasks (before filtering).
    const chipMap = new Map();
    for (const item of all) {
        for (const tag of item.tagList) {
            const chip = chipMap.get(tag.id) ?? {
                id: tag.id,
                label: tag.name,
                count: 0,
                sort_key: tag.sort_key,
            };
            if (!item.done)
                chip.count += 1;
            chipMap.set(tag.id, chip);
        }
    }
    // Taxonomy tags keep the taxonomy's order; persons of a field go by name (docs/04 §5 rule 3).
    const chips = [...chipMap.values()]
        .sort((a, b) => groupField
        ? a.label.localeCompare(b.label, 'es')
        : a.sort_key < b.sort_key
            ? -1
            : a.sort_key > b.sort_key
                ? 1
                : a.label.localeCompare(b.label))
        .map(({ id, label, count }) => ({ id, label, count }));
    const untaggedItems = all.filter((item) => item.tagList.length === 0);
    const untagged = untaggedItems.filter((item) => !item.done).length;
    const visible = all.filter((item) => matches(item, input.selected));
    const progress = {
        done: visible.filter((item) => item.done).length,
        total: visible.length,
    };
    const pinned = input.pinTop ? visible.filter((item) => input.highlighted.has(item.id)) : [];
    const pinnedIds = new Set(pinned.map((item) => item.id));
    const rest = visible.filter((item) => !pinnedIds.has(item.id));
    // Completed tasks by mode: in their group (struck), in the section, or hidden.
    const inGroups = [];
    const doneSection = [];
    let hiddenDone = 0;
    for (const item of rest) {
        if (!item.done ||
            input.display === 'strike' ||
            (input.completedHere.has(item.id) && input.display === 'section')) {
            inGroups.push(item);
        }
        else if (input.display === 'section') {
            doneSection.push(item);
        }
        else if (input.revealHidden) {
            doneSection.push(item);
        }
        else {
            hiddenDone += 1;
        }
    }
    const groups = [];
    if (input.groupBy === null) {
        groups.push({ key: null, label: null, items: inGroups });
    }
    else {
        const byTag = new Map();
        for (const chip of chips)
            byTag.set(chip.id, { key: chip.id, label: chip.label, items: [] });
        const none = { key: null, label: null, items: [] };
        for (const item of inGroups) {
            if (item.tagList.length === 0) {
                none.items.push(item);
                continue;
            }
            for (const tag of item.tagList) {
                if (input.selected.length > 0 && !input.selected.includes(tag.id))
                    continue;
                byTag.get(tag.id)?.items.push(item);
            }
        }
        for (const group of byTag.values())
            if (group.items.length > 0)
                groups.push(group);
        if (none.items.length > 0)
            groups.push(none);
    }
    return {
        pinned,
        groups,
        doneSection,
        hiddenDone,
        chips,
        untagged,
        hasUntagged: untaggedItems.length > 0,
        progress,
    };
}
//# sourceMappingURL=view-model.js.map